/* iter460 — LactationCoachEntryCard.
 *
 * Discovery-only entry tile placed in the Health Intelligence page. Hidden by
 * default for everyone; shows up only when one of these is true:
 *   1. User already created a lactation_profile with status != "none"
 *      → shows current status + quick link "Open Lactation Coach"
 *   2. User has self-disclosed gender=female AND is between 18-50 → shows
 *      a discreet "Pregnant or breastfeeding?" discovery card with opt-in
 *
 * Per product spec (visibility option A) this is **opt-in**, never pushed.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Baby, ArrowRight, Sparkles } from "lucide-react";
import PerinatalDilCard from "./PerinatalDilCard";
import GenerateLactationMenuButton from "./GenerateLactationMenuButton";

const STATUS_LABEL = {
  pregnant:           { bg: "Бременна",           en: "Pregnant" },
  postpartum_0_3mo:   { bg: "Кърмя 0-3 месеца",    en: "Breastfeeding 0-3mo" },
  postpartum_3_6mo:   { bg: "Кърмя 3-6 месеца",    en: "Breastfeeding 3-6mo" },
  postpartum_6_12mo:  { bg: "Кърмя 6-12 месеца",   en: "Breastfeeding 6-12mo" },
  weaning:            { bg: "В отбиване",          en: "Weaning" },
};

export default function LactationCoachEntryCard() {
  const { user, API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language !== "en";
  const [profile, setProfile] = useState(null);

  // Eligibility — show discovery only to self-identified female users 18-50.
  const personal = user?.profile?.personal || {};
  const gender = (personal.gender || "").toLowerCase();
  const age = personal.age || (personal.dob
    ? Math.floor((Date.now() - new Date(personal.dob)) / (365.25 * 24 * 3600 * 1000))
    : null);
  const eligible = user?.role === "admin" ||
    (["female", "f", "жена"].includes(gender) && age && age >= 18 && age <= 50);

  useEffect(() => {
    if (!eligible) return;
    let cancelled = false;
    axios.get(`${API}/lactation/profile`)
      .then(({ data }) => { if (!cancelled) setProfile(data); })
      .catch(() => { /* not configured yet — show discovery */ });
    return () => { cancelled = true; };
  }, [API, eligible]);

  if (!eligible) return null;

  const isActive = profile && profile.status && profile.status !== "none";
  const cardCls = isDark
    ? "bg-[hsl(223,47%,9%)] border-[hsl(223,47%,16%)]"
    : "bg-white border-slate-100 shadow-sm";
  const textColor = isDark ? "text-white" : "text-slate-900";
  const subColor = isDark ? "text-slate-400" : "text-slate-600";

  return (
    <div className="mb-6 space-y-3">
      <Link
        to="/dashboard/lactation"
        data-testid="lactation-coach-entry-card"
        className={`block rounded-2xl border p-4 transition-all hover:shadow-md hover:border-pink-300 ${cardCls}`}
      >
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-gradient-to-br from-rose-400 to-pink-600 shadow-sm">
          <Baby className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className={`text-sm font-bold ${textColor}`}>
              {bg ? "Mama Coach" : "Mama Coach"}
            </h3>
            {isActive && (
              <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-pink-500/20 text-pink-600">
                {bg ? STATUS_LABEL[profile.status]?.bg : STATUS_LABEL[profile.status]?.en}
              </span>
            )}
            {profile?.mastitis_active && (
              <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-orange-500/20 text-orange-600">
                {bg ? "Мастит" : "Mastitis"}
              </span>
            )}
          </div>
          <p className={`text-xs mt-0.5 ${subColor}`}>
            {isActive
              ? (bg
                  ? "Виж дневните цели и mastitis план"
                  : "View daily targets and mastitis plan")
              : (bg
                  ? "Подкрепа при кърмене, мастит, хранене и хидратация. Educational."
                  : "Support for breastfeeding, mastitis, nutrition and hydration. Educational.")}
          </p>
        </div>
        <ArrowRight className={`w-4 h-4 ${subColor}`} />
      </div>
    </Link>

      {/* Personalised perinatal DIL ceiling — renders only when active */}
      <PerinatalDilCard />
      <GenerateLactationMenuButton />
    </div>
  );
}
